import React, { useEffect, useState } from "react";
import Select from "react-select";
import {
    Button,
    FormGroup,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Spinner,
} from "reactstrap";
import { toast } from "react-toastify";
import {
    fetchBadFitTrackingDetails,
    fetchCommonBadFitResolutionReasons,
    mapBadFitResolutionReasonOptionsFromResponse,
    resolveBadFitForCandidate,
} from "model/BadFitModel";

const reactSelectMenuPortalStyles = {
    menuPortal: (baseStyles) => ({ ...baseStyles, zIndex: 9999 }),
    menu: (baseStyles) => ({ ...baseStyles, zIndex: 9999 }),
};

const mapTrackingDetailsFromResponse = (response) => {
    const data = response?.data;
    if (!data || typeof data !== "object") {
        return {
            candidateClientId: "",
            note: "",
        };
    }

    const note =
        String(data?.originalReasonText ?? data?.note ?? data?.notes ?? "").trim();

    return {
        candidateClientId: String(data?.candidateClientId ?? "").trim(),
        candidateName: String(data?.candidateName ?? "").trim(),
        clientName: String(data?.clientName ?? "").trim(),
        note,
    };
};

const CallDetailsBadFitResolutionModal = ({
    isOpen,
    onClose,
    candidateClientId = "",
    badFitTrackingId = null,
    onSuccess,
}) => {
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [note, setNote] = useState("");
    const [resolvedCandidateClientId, setResolvedCandidateClientId] = useState("");
    const [trackingSummary, setTrackingSummary] = useState({ candidateName: "", clientName: "" });
    const [reasonOptions, setReasonOptions] = useState([]);
    const [reasonsLoading, setReasonsLoading] = useState(false);
    const [selectedReason, setSelectedReason] = useState(null);

    useEffect(() => {
        if (!isOpen) return undefined;

        setNote("");
        setResolvedCandidateClientId("");
        setTrackingSummary({ candidateName: "", clientName: "" });
        setSelectedReason(null);
        setReasonOptions([]);

        const trackingId = String(badFitTrackingId ?? "").trim();
        if (!trackingId) {
            toast.error("Bad fit tracking ID not found.");
            return undefined;
        }

        let requestCancelled = false;

        (async () => {
            setLoading(true);
            setReasonsLoading(true);

            try {
                const [trackingResponse, reasonsResponse] = await Promise.all([
                    fetchBadFitTrackingDetails(trackingId),
                    fetchCommonBadFitResolutionReasons(),
                ]);

                if (requestCancelled) return;

                if (trackingResponse?.success === false) {
                    toast.error(trackingResponse?.message || "Unable to load bad fit details.");
                } else if (trackingResponse?.data) {
                    const trackingDetails = mapTrackingDetailsFromResponse(trackingResponse);
                    setNote(trackingDetails.note);
                    setResolvedCandidateClientId(
                        trackingDetails.candidateClientId ||
                            String(candidateClientId ?? "").trim()
                    );
                    setTrackingSummary({
                        candidateName: trackingDetails.candidateName,
                        clientName: trackingDetails.clientName,
                    });
                }

                setReasonOptions(mapBadFitResolutionReasonOptionsFromResponse(reasonsResponse));
            } catch {
                if (!requestCancelled) {
                    setNote("");
                    setResolvedCandidateClientId("");
                    setTrackingSummary({ candidateName: "", clientName: "" });
                    setReasonOptions([]);
                }
            } finally {
                if (!requestCancelled) {
                    setLoading(false);
                    setReasonsLoading(false);
                }
            }
        })();

        return () => {
            requestCancelled = true;
        };
    }, [isOpen, candidateClientId, badFitTrackingId]);

    const handleClose = () => {
        if (!submitting) onClose?.();
    };

    const handleSubmit = async () => {
        const id = String(resolvedCandidateClientId || candidateClientId || "").trim();
        if (!id) {
            toast.error("Candidate ID not found.");
            return;
        }

        const badFitResolutionReasonMasterId = String(selectedReason?.value ?? "").trim();
        if (!badFitResolutionReasonMasterId) {
            toast.error("Please select a resolution reason.");
            return;
        }

        setSubmitting(true);
        try {
            const response = await resolveBadFitForCandidate(id, {
                badFitResolutionReasonMasterId,
                note: String(note ?? "").trim(),
            });

            if (response?.success) {
                toast.success(response?.message || "Bad fit resolved successfully.");
                await onSuccess?.();
                onClose?.();
            } else if (response?.message) {
                toast.error(response.message);
            }
        } finally {
            setSubmitting(false);
        }
    };

    const isBusy = loading || submitting || reasonsLoading;

    return (
        <Modal
            isOpen={isOpen}
            toggle={handleClose}
            centered
            backdrop="static"
            size="md"
            zIndex={5000}
            className="cd-bad-fit-resolution-modal modal-full-height"
        >
            <ModalHeader toggle={handleClose} className="cd-bad-fit-resolution-modal__header">
                <span className="cd-bad-fit-resolution-modal__title">
                    <i className="mdi mdi-account-alert-outline me-2" aria-hidden />
                    Bad Fit Resolution
                </span>
            </ModalHeader>
            <ModalBody className="cd-bad-fit-resolution-modal__body">
                {loading ? (
                    <div className="text-center py-4">
                        <Spinner />
                    </div>
                ) : (
                    <>
                        {trackingSummary.candidateName || trackingSummary.clientName ? (
                            <div className="cd-bad-fit-resolution-summary mb-3">
                                {trackingSummary.candidateName ? (
                                    <div className="mb-1">
                                        <span className="text-muted me-1">Candidate:</span>
                                        <span className="fw-semibold">
                                            {trackingSummary.candidateName}
                                        </span>
                                    </div>
                                ) : null}
                                {trackingSummary.clientName ? (
                                    <div>
                                        <span className="text-muted me-1">Client:</span>
                                        <span className="fw-semibold">{trackingSummary.clientName}</span>
                                    </div>
                                ) : null}
                            </div>
                        ) : null}

                        <FormGroup className="mb-3">
                            <Label className="fw-semibold">Original Reason</Label>
                            <div className="border rounded p-3 bg-light text-break">
                                {note ? note : "—"}
                            </div>
                        </FormGroup>

                        <FormGroup className="mb-0">
                            <Label className="fw-semibold">Resolution Reason</Label>
                            <Select
                                className="react-select-container"
                                classNamePrefix="react-select"
                                options={reasonOptions}
                                value={selectedReason}
                                onChange={setSelectedReason}
                                isLoading={reasonsLoading}
                                isDisabled={submitting}
                                isClearable
                                placeholder="Select resolution reason"
                                menuPortalTarget={
                                    typeof document !== "undefined" ? document.body : null
                                }
                                styles={reactSelectMenuPortalStyles}
                                menuPosition="fixed"
                            />
                        </FormGroup>
                    </>
                )}
            </ModalBody>
            <ModalFooter className="cd-bad-fit-resolution-modal__footer">
                <Button color="secondary" type="button" onClick={handleClose} disabled={submitting}>
                    Cancel
                </Button>
                <Button
                    color="primary"
                    type="button"
                    onClick={handleSubmit}
                    disabled={isBusy || !selectedReason}
                >
                    {submitting ? (
                        <>
                            <Spinner size="sm" className="me-1" />
                            Submitting…
                        </>
                    ) : (
                        "Submit"
                    )}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default CallDetailsBadFitResolutionModal;
